/**
 * ss-seamless-loop.js — Full-width infinite marquee
 * Measures loop distance including flex gap so cards never overlap at the seam.
 */
(function () {
  'use strict';

  var registry = new WeakMap();

  function clearClones(track) {
    var nodes = track.querySelectorAll('[data-seamless-clone], [data-seamless-seed]');
    for (var i = 0; i < nodes.length; i++) nodes[i].remove();
  }

  function whenImagesReady(root, cb) {
    var imgs = root.querySelectorAll('img');
    if (!imgs.length) {
      cb();
      return;
    }
    var pending = imgs.length;
    function done() {
      pending -= 1;
      if (pending <= 0) cb();
    }
    imgs.forEach(function (img) {
      if (img.complete) done();
      else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }

  function getPrimaryGroup(track) {
    return track.querySelector('[data-seamless-group]:not([data-seamless-clone]):not([data-seamless-seed])');
  }

  function getLoopWidth(track, group) {
    var gw = group.getBoundingClientRect().width;
    if (gw < 1) return 0;
    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return gw + gap;
  }

  function fillTrack(track, group) {
    clearClones(track);
    var viewport = track.parentElement || track;
    var vw = viewport.getBoundingClientRect().width || window.innerWidth || 1200;
    var loopW = getLoopWidth(track, group);
    if (loopW < 1) return false;
    var need = Math.max(vw * 2.5, loopW * 3);
    var guard = 0;
    while (track.scrollWidth < need && guard < 40) {
      var clone = group.cloneNode(true);
      clone.setAttribute('data-seamless-clone', 'true');
      clone.removeAttribute('data-seamless-group');
      clone.removeAttribute('data-seamless-seed');
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
      guard++;
    }
    return true;
  }

  function stopLoop(track) {
    var state = registry.get(track);
    if (state) {
      state.active = false;
      if (state.resizeHandler) {
        window.removeEventListener('resize', state.resizeHandler);
      }
      registry.delete(track);
    }
    clearClones(track);
    track.dataset.seamlessInit = 'false';
    track.style.transform = '';
  }

  function initLoop(track) {
    if (!track) return;
    stopLoop(track);

    var group = getPrimaryGroup(track);
    if (!group) return;

    track.dataset.seamlessInit = 'false';

    var durationSec = parseFloat(track.dataset.seamlessSpeed || '45');
    if (durationSec < 5) durationSec = 45;
    var direction = track.dataset.seamlessDirection === 'right' ? -1 : 1;
    var state = {
      active: true,
      paused: false,
      groupWidth: 0,
      pos: 0,
      pxPerFrame: 0,
      bootAttempts: 0,
      resizeHandler: null
    };
    registry.set(track, state);

    track.style.animation = 'none';
    track.style.willChange = 'transform';
    track.style.display = 'flex';
    track.style.width = 'max-content';

    function measure() {
      if (!state.active) return;
      fillTrack(track, group);
      state.groupWidth = getLoopWidth(track, group);
      if (state.groupWidth > 0) {
        state.pxPerFrame = state.groupWidth / (durationSec * 60);
        if (direction === -1 && state.pos === 0) state.pos = state.groupWidth;
        if (direction === 1 && state.pos >= state.groupWidth) state.pos = state.pos % state.groupWidth;
        if (direction === -1 && state.pos <= 0) state.pos = state.groupWidth;
      }
    }

    function bootMeasure() {
      measure();
      state.bootAttempts += 1;
      if (state.groupWidth < 1 && state.bootAttempts < 120) {
        requestAnimationFrame(bootMeasure);
      } else if (state.groupWidth > 0) {
        track.dataset.seamlessInit = 'true';
      }
    }

    bootMeasure();
    whenImagesReady(track, function () {
      measure();
      if (state.groupWidth > 0) track.dataset.seamlessInit = 'true';
      setTimeout(measure, 120);
      setTimeout(measure, 400);
    });

    if (direction === -1 && state.groupWidth > 0) state.pos = state.groupWidth;

    var resizeTimer;
    state.resizeHandler = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 100);
    };
    window.addEventListener('resize', state.resizeHandler, { passive: true });

    function tick() {
      if (!state.active) return;
      if (state.groupWidth < 1 && state.bootAttempts < 240) {
        measure();
        state.bootAttempts += 1;
      }
      if (!state.paused && state.groupWidth > 0 && state.pxPerFrame > 0) {
        state.pos += state.pxPerFrame * direction;
        if (direction === 1) {
          if (state.pos >= state.groupWidth) state.pos -= state.groupWidth;
        } else {
          if (state.pos <= 0) state.pos += state.groupWidth;
        }
        track.style.setProperty('transform', 'translate3d(' + (-state.pos).toFixed(2) + 'px,0,0)');
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    track.addEventListener('mouseenter', function () { state.paused = true; });
    track.addEventListener('mouseleave', function () { state.paused = false; });
    track.addEventListener('focusin', function () { state.paused = true; });
    track.addEventListener('focusout', function () { state.paused = false; });
    track.addEventListener('touchstart', function () { state.paused = true; }, { passive: true });
    track.addEventListener('touchend', function () { state.paused = false; }, { passive: true });

    document.addEventListener('ss:qv-open', function () { state.paused = true; });
    document.addEventListener('ss:qv-close', function () { state.paused = false; });
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-seamless-loop]').forEach(initLoop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  window.addEventListener('load', function () { boot(); });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('ss:age-verified', function () {
    setTimeout(function () { boot(); }, 80);
  });
})();
